import React from 'react';
import PropTypes from 'prop-types';

import { Icon, OptionalSection, Chip } from '@weezevent/nacre';
import Style from './sessionsList.module.css';

import { useTranslation } from 'react-i18next';
import useAppContext from '../../../../../context/useAppContext';
import moment from 'moment/moment';
import i18n from 'i18next';
import LabelDetails, { LabelDetailsContainer } from '../../../../attendee/detail/components/label-details/labelDetails';
import TransactionDetails from '../../../../attendee/detail/components/transaction-details/transactionDetails';

const formatAndLocalizeDate = (dateString, timezone, locale) => {
    if (!dateString) {
        return '';
    }

    let dateLocalized = moment.tz(
        moment
            .utc(dateString, moment.ISO_8601)
            .tz(timezone)
            .format('YYYY-MM-DD HH:mm'),
        timezone
    );

    return Intl.DateTimeFormat(locale, { dateStyle: 'full', timeStyle: 'short' }).format(new Date(dateLocalized));
};

function HeaderSessionsList({ sessions, onClick = () => {} }) {
    const { t } = useTranslation();

    if (!sessions.length) {
        return null;
    }

    return (
        <div className={Style['header']}>
            <div className={Style['title-container']}>
                <span className={Style['title']}>{t('ticket.common.sessions', { count: sessions.length })}</span>
                <Chip primary icon="calendar-star" text={`x${sessions.length}`} />
            </div>
            <div className={Style['edit']} onClick={onClick}>
                <span className={Style['edit-label']}>{t('ticket.manager.attendees.action.swap_sessions.dropdownItem')}</span>
                <Icon name="edit" className={Style['edit-icon']} />
            </div>
        </div>
    );
}

function SessionDetails({ session }) {
    const { t } = useTranslation();

    if (!session) {
        return null;
    }

    return (
        <TransactionDetails.Item title={session.formatted_start_date}>
            <div className={Style['count-tickets']}>{t('ticket.manager.attendees.detail.sessionsList.countTickets', { count: session.count })}</div>
        </TransactionDetails.Item>
    );
}

function TotalTicketsSessions({ count }) {
    const { t } = useTranslation();

    if (!count) {
        return null;
    }

    return (
        <TransactionDetails.Item title={t('ticket.manager.attendees.detail.sessionsList.labelCountTickets')} blue>
            <div className={Style['count-tickets']}>{t('ticket.manager.attendees.detail.sessionsList.countTickets', { count: count })}</div>
        </TransactionDetails.Item>
    );
}

export default function SessionsList({ order }) {
    const { t } = useTranslation();
    const [{ event, event_timezone }] = useAppContext();
    const locale = i18n.language;
    const [openDetails, setOpenDetails] = React.useState(false);

    const sessions = React.useMemo(() => {
        let _sessions = {};

        if (!event.use_sessions) {
            return [];
        }

        order.attendees.forEach(attendee => {
            if (attendee?.booking && attendee.booking.inventory_weez_date_id && attendee.booking.inventory_start_date) {
                if (_sessions[attendee.booking.inventory_weez_date_id]) {
                    _sessions[attendee.booking.inventory_weez_date_id].count += 1;
                } else {
                    _sessions[attendee.booking.inventory_weez_date_id] = {
                        count: 1,
                        start_date: attendee.booking.inventory_start_date,
                        formatted_start_date: formatAndLocalizeDate(attendee.booking.inventory_start_date, event_timezone, locale)
                    };
                }
            }
        });

        return Object.values(_sessions).sort((s1, s2) => new Date(s1.start_date).getTime() - new Date(s2.start_date).getTime());
    }, [order.attendees, locale, event_timezone]);

    const firstSessionDate = React.useMemo(() => {
        if (sessions.length > 0) {
            return sessions[0].formatted_start_date;
        }
    }, [sessions]);

    const lastSessionDate = React.useMemo(() => {
        if (sessions.length > 1) {
            return sessions[sessions.length - 1].formatted_start_date;
        }
    }, [sessions]);

    const countTicketsWithSessions = React.useMemo(() => {
        return sessions.reduce((count, session) => session.count + count, 0);
    }, [sessions]);

    if (!event.use_sessions || !sessions.length) {
        return null;
    }

    return (
        <div className={Style['container']}>
            <div className={Style['content']}>
                <HeaderSessionsList sessions={sessions} />
                <LabelDetailsContainer>
                    <LabelDetails title={sessions.length === 1 ? t('ticket.event.start_date') : t('ticket.manager.attendees.detail.sessionsList.firstSession')}>
                        {firstSessionDate}
                    </LabelDetails>
                    {sessions.length === 1 && <LabelDetails title={t('ticket.manager.attendees.detail.sessionsList.labelNumberTickets')}>{countTicketsWithSessions}</LabelDetails>}
                    {sessions.length > 1 && <LabelDetails title={t('ticket.manager.attendees.detail.sessionsList.lastSession')}>{lastSessionDate}</LabelDetails>}
                </LabelDetailsContainer>
            </div>
            {sessions.length > 1 && (
                <div className={`${Style['footer']} ${openDetails ? Style['footer-open'] : ''}`}>
                    <OptionalSection open={openDetails} label={t('ticket.manager.attendees.detail.sessionsList.labelOpenDetails')} onToggle={open => setOpenDetails(open)}>
                        {sessions.map(session => (
                            <SessionDetails key={session.start_date} session={session} />
                        ))}
                        <TotalTicketsSessions count={countTicketsWithSessions} />
                    </OptionalSection>
                </div>
            )}
        </div>
    );
}

SessionsList.propTypes = {
    order: PropTypes.object.isRequired
};
